import { AlignVAT, ZeroDisplayType } from "@vp/ep-pricing";

export const PRICE_UPDATE_EVENT = "price-update";

export interface Price {
  taxed: number;
  untaxed: number;
}

export interface PriceProperties {
  minimumFractionDigits: number;
  currency: string;
  vatInc: boolean;
  quantity?: number;
  moq?: number;
  hideVatMessage?: boolean;
  hideShippingMessageInVatMessage?: boolean;
  hideMinimumOrderableQuantity?: boolean;
  alwaysShowMOQInfo?: boolean;
  isPriceNegative?: boolean;
  zeroDisplayType?: ZeroDisplayType;
  showComparativeDisplay?: boolean;
  isSetupFeeIncluded?: boolean;
  alignVAT?: AlignVAT;
  hideSetupFee?: boolean;
  isTile?: boolean;
  doesOfferExist?: boolean;
  hidePriceDetails?: boolean;
}

// If you add a new API-driven value here, also add it to PricePresentationType in ../prices.ts.
export enum PriceDisplayType {
  UnitRangePrice = "unitRangePrice",
  PriceInfo = "priceInfo",
  UnitPriceInfo = "unitPriceInfo",
  FromPrice = "fromPrice",
  UnitFromPrice = "unitFromPrice",
  DifferentialUnitRangePrice = "differentialUnitRangePrice",
  DifferentialPrice = "differentialPrice",
  UnitDifferentialPrice = "unitDifferentialPrice",
  DifferentialPriceForQuantity = "differentialPriceForQuantity",
  /**
   * Client-side only — not an API display strategy, never passed to queuePriceQuery.
   * Used internally by PriceExtractionWrapper when doc mode is active
   * (docRefUrl or assumeDocRequiresCertification) to render only the
   * Logo Processing Charge (Art Validation Fee) via DifferentialPrice.
   */
  LogoProcessingCharge = "logoProcessingCharge",
}

export enum DisplayVariant {
  FROM = "from",
  LOWEST_PRICE = "lowestPrice",
}

export type ProductPricingInput = {
  mpvId?: string;
  productId?: string;
  version?: number;
  selections?: SelectionsWithQuantity;
  alternateSelections?: SelectionsWithQuantity;
  displayVariant?: DisplayVariant;
  requestedPriceDisplayStrategy?: PriceDisplayType;
  hideMinimumOrderableQuantity?: boolean;
  alwaysShowMOQInfo?: boolean; //The prop will enable to show "No minimum quantity" when MOQ=1 if true
  hidePriceDetails?: boolean;
  /**
   * URL of the document reference used by Website Pricing to determine
   * whether a Logo Processing Charge (Art Validation Fee) applies.
   * Takes precedence over `assumeDocRequiresCertification` when both are provided.
   */
  docRefUrl?: string;
  /**
   * Fallback boolean used when `docRefUrl` is not yet available (e.g. new documents).
   * When `true`, signals that the document likely requires certification and a charge may apply.
   * Ignored when `docRefUrl` is provided.
   */
  assumeDocRequiresCertification?: boolean;
};

export type SelectionsWithQuantity = Record<string, string> & { quantity?: string };

export interface Prices {
  baseListPrice?: Price;
  baseDiscountedPrice?: Price;
  totalListPrice?: Price;
  totalDiscountedPrice?: Price;
  unitListPrice?: Price;
  unitDiscountedPrice?: Price;
  unitPriceForMoq?: Price;
  listPriceForMoq?: Price;
  priceDifferenceAtMaximumPrice?: Price;
  discountedPriceDifferenceAtMaximumPrice?: Price;
  priceDifferenceAtMinimumPrice?: Price;
  discountedPriceDifferenceAtMinimumPrice?: Price;
  differentialListPrice?: Price;
  differentialDiscountedPrice?: Price;
  differentialUnitListPrice?: Price;
  differentialUnitDiscountedPrice?: Price;
  setupFeeListPrice?: Price;
  setupFeeDiscountedPrice?: Price;
  highestListPrice?: Price;
  highestDiscountedPrice?: Price;
  lowestListPrice?: Price;
  lowestDiscountedPrice?: Price;
  volumeDiscount?: Price;
  /** List price of the Logo Processing Charge (Art Validation Fee), when applicable */
  logoProcessingChargeListPrice?: Price;
  /** Discounted price of the Logo Processing Charge, when applicable */
  logoProcessingChargeDiscountedPrice?: Price;
}
